import React, { useState, useRef, useEffect } from 'react';
import { arrayOf, bool, object } from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { FormattedMessage, injectIntl, intlShape } from '../../util/reactIntl';
import { isScrollingDisabled } from '../../ducks/UI.duck';
import { getListingsById } from '../../ducks/marketplaceData.duck';
import { ensureUser } from '../../util/data';
import config from '../../config';
import {
  Page,
  LayoutSingleColumn,
  LayoutWrapperTopbar,
  LayoutWrapperMain,
  LayoutWrapperFooter,
  Footer,
  NamedLink,
  JoinPreFooter,
  AvatarMedium,
} from '../../components';
import { TopbarContainer } from '../../containers';
import { BsFillCheckCircleFill, BsArrowLeftCircle, BsArrowRightCircle } from 'react-icons/bs';
import { FaStar } from 'react-icons/fa';
import { BiSolidQuoteAltRight } from 'react-icons/bi';
import { IoIosArrowDown } from 'react-icons/io';

import { skillSeoPageContents } from '../../skillSeoPageContents';
import { buildFreelancerItemListSchema } from './skillSeoSchema';
import { loadData, MIN_RESULTS_TO_SHOW, MAX_RESULTS } from './SkillSeoPage.duck';

import facebookImage from '../../assets/freedomlySocialShare.png';
import twitterImage from '../../assets/freedomlySocialShare.png';
import heroImage1 from '../../assets/skill-seo-hero-1.jpg';
import heroImage2 from '../../assets/skill-seo-hero-2.jpg';
import heroImage3 from '../../assets/skill-seo-hero-3.jpg';

import testimonialImage1 from '../../assets/systam-success-story-quote2.webp';
import testimonialImage2 from '../../assets/haltian-success-story-quote.png';
import testimonialImage3 from '../../assets/corego-success-story-quote.png';

import hartwallLogo from '../../assets/hartwall-logo.svg';
import invesdorLogo from '../../assets/invesdor-logo.svg';
import peikkoLogo from '../../assets/peikko-group-logo.png';
import haltianLogo from '../../assets/haltian-logo.png';
import eerikkilaLogo from '../../assets/eerikkila-logo.svg';
import helsinkiLogo from '../../assets/helsinki-logo.png';
import coregoLogo from '../../assets/corego-logo.png';
import zervantLogo from '../../assets/zervant-logo.svg';
import systamLogo from '../../assets/systam-logo.svg';

import css from './SkillSeoPage.css';

const HERO_LOGOS = [
  { id: 'invesdor', src: invesdorLogo },
  { id: 'hartwall', src: hartwallLogo },
  { id: 'peikko', src: peikkoLogo },
  { id: 'haltian', src: haltianLogo },
  { id: 'eerikkila', src: eerikkilaLogo },
  { id: 'helsinki', src: helsinkiLogo },
  { id: 'corego', src: coregoLogo },
  { id: 'zervant', src: zervantLogo },
  { id: 'systam', src: systamLogo },
];

const TESTIMONIALS = [
  {
    textId: 'SolutionPageCustomerReviews.reviewText1',
    nameId: 'SolutionPageCustomerReviews.reviewName1',
    titleId: 'SolutionPageCustomerReviews.reviewTitle1',
    img: testimonialImage1,
  },
  {
    textId: 'SolutionPageCustomerReviews.reviewText2',
    nameId: 'SolutionPageCustomerReviews.reviewName2',
    titleId: 'SolutionPageCustomerReviews.reviewTitle2',
    img: testimonialImage2,
  },
  {
    textId: 'SolutionPageCustomerReviews.reviewText4',
    nameId: 'SolutionPageCustomerReviews.reviewName4',
    titleId: 'SolutionPageCustomerReviews.reviewTitle4',
    img: testimonialImage3,
  },
];

const HOW_IT_WORKS_STEPS = [1, 2, 3].map(i => ({
  titleId: `StartupSolutionPage.howItWorksCardTitle${i}`,
  textId: `StartupSolutionPage.howItWorksCardText${i}`,
  checkId: `StartupSolutionPage.howItWorksCardCheckmarkText${i}`,
}));

const MAX_CARD_PILLS = 4;

// Formats a translated message into plain text for JSON-LD (strips <b> and
// turns {lineBreak} tokens into spaces).
const formatPlainText = (intl, id) => {
  const joinChunks = chunks => (Array.isArray(chunks) ? chunks.join('') : chunks);
  const formatted = intl.formatMessage({ id }, { lineBreak: ' ', b: joinChunks });
  return Array.isArray(formatted) ? formatted.join('') : formatted;
};

const SatisfactionCard = () => (
  <div className={css.sideCard}>
    <div className={css.satisfactionTitleRow}>
      <FaStar className={css.satisfactionStar} />
      <h3 className={css.sideCardTitle}>
        <FormattedMessage id="SkillSeoPage.satisfactionTitle" />
      </h3>
    </div>
    <p className={css.sideCardText}>
      <FormattedMessage id="SkillSeoPage.satisfactionText" />
    </p>
  </div>
);

const TestimonialsCard = () => {
  const [index, setIndex] = useState(0);
  const count = TESTIMONIALS.length;
  const current = TESTIMONIALS[index];

  return (
    <div className={css.testimonialsCard}>
      <h3 className={css.testimonialsTitle}>
        <FormattedMessage id="SkillSeoPage.testimonialsTitle" />
      </h3>
      <div className={css.testimonialInnerCard}>
        <p className={css.testimonialQuote}>
          <FormattedMessage id={current.textId} />
        </p>
        <div className={css.testimonialAuthorRow}>
          <img className={css.testimonialAvatar} src={current.img} alt="" loading="lazy" />
          <div>
            <div className={css.testimonialAuthorName}>
              <FormattedMessage id={current.nameId} />
            </div>
            <div className={css.testimonialAuthorTitle}>
              <FormattedMessage id={current.titleId} />
            </div>
          </div>
        </div>
      </div>
      <div className={css.testimonialArrows}>
        <button
          type="button"
          className={css.testimonialArrowButton}
          aria-label="Previous"
          onClick={() => setIndex((index - 1 + count) % count)}
        >
          <BsArrowLeftCircle />
        </button>
        <button
          type="button"
          className={css.testimonialArrowButton}
          aria-label="Next"
          onClick={() => setIndex((index + 1) % count)}
        >
          <BsArrowRightCircle />
        </button>
      </div>
    </div>
  );
};

const WhyFreedomlyCard = () => (
  <div className={css.sideCard}>
    <h3 className={css.sideCardTitle}>
      <FormattedMessage id="SkillSeoPage.whyFreedomlyTitle" />
    </h3>
    <div className={css.whyFreedomlyItems}>
      {[1, 2, 3].map(i => (
        <div key={i} className={css.whyFreedomlyItem}>
          <BsFillCheckCircleFill className={css.checkIcon} />
          <FormattedMessage id={`SkillSeoPage.whyFreedomlyItem${i}`} />
        </div>
      ))}
    </div>
  </div>
);

const FreelancerCard = props => {
  const { listing, reviews, testimonials, pillLabels, isAuthenticated } = props;
  const author = ensureUser(listing.author);
  const publicData = listing.attributes.publicData || {};
  const displayName = author.attributes.profile?.displayName || 'Freelancer';

  const locationCity = publicData.locationCity || '';
  const locationCountry = publicData.locationCountry || '';
  const location = [locationCity, locationCountry].filter(Boolean).join(', ');

  const billedTotal = author.attributes.profile?.metadata?.billedTotal;
  let billedTier = null;
  if (billedTotal != null && billedTotal > 0) {
    const tiers = [100000, 50000, 25000, 10000, 5000, 1000];
    const tier = tiers.find(t => billedTotal >= t);
    if (tier) {
      billedTier = `€${tier / 1000}K+`;
    }
  }

  const hasReviews = reviews && reviews.length > 0;
  const averageRating = hasReviews
    ? (reviews.reduce((sum, r) => sum + (r?.attributes?.rating || 0), 0) / reviews.length).toFixed(
        1
      )
    : null;
  const hasTestimonials = testimonials && testimonials.length > 0;
  const hasStats = hasReviews || billedTier || hasTestimonials;

  const visiblePills = pillLabels.slice(0, MAX_CARD_PILLS);
  const hiddenPillCount = pillLabels.length - visiblePills.length;

  return (
    <div className={css.freelancerCard}>
      <div className={css.freelancerCardTopRow}>
        <div className={css.freelancerAvatarWrapper}>
          <AvatarMedium className={css.freelancerAvatar} user={author} disableProfileLink={true} />
        </div>
        <div className={css.freelancerNameColumn}>
          <div className={css.freelancerName}>{displayName}.</div>
          {location ? <div className={css.freelancerLocation}>{location}</div> : null}
        </div>
        {hasStats ? (
          <>
            <div className={css.freelancerCardDivider} />
            <div className={css.freelancerStats}>
              {hasReviews ? (
                <div className={css.statItem}>
                  <div className={css.statValueRow}>
                    <FaStar className={css.statStarIcon} />
                    <span className={css.statValue}>{averageRating}</span>
                    <span className={css.statCount}>({reviews.length})</span>
                  </div>
                  <span className={css.statLabel}>
                    <FormattedMessage id="FreelancerProfileUserCard.reviews" />
                  </span>
                </div>
              ) : null}
              {billedTier ? (
                <div className={css.statItem}>
                  <div className={css.statValueRow}>
                    <span className={css.statValue}>{billedTier}</span>
                  </div>
                  <span className={css.statLabel}>
                    <FormattedMessage id="FreelancerProfileUserCard.earnings" />
                  </span>
                </div>
              ) : null}
              {hasTestimonials ? (
                <div className={css.statItem}>
                  <div className={css.statValueRow}>
                    <BiSolidQuoteAltRight className={css.statQuoteIcon} />
                    <span className={css.statValue}>{testimonials.length}</span>
                  </div>
                  <span className={css.statLabel}>
                    <FormattedMessage id="FreelancerProfileUserCard.testimonials" />
                  </span>
                </div>
              ) : null}
            </div>
          </>
        ) : null}
      </div>
      {visiblePills.length > 0 ? (
        <div className={css.freelancerPills}>
          {visiblePills.map(label => (
            <div key={label} className={css.freelancerPill}>
              {label}
            </div>
          ))}
          {hiddenPillCount > 0 ? (
            <div className={css.freelancerPillMore}>+{hiddenPillCount}</div>
          ) : null}
        </div>
      ) : null}
      {isAuthenticated ? (
        <NamedLink name="ProfilePage" params={{ id: author.id.uuid }} className={css.primaryButton}>
          <FormattedMessage id="SkillSeoPage.viewProfile" />
        </NamedLink>
      ) : (
        <NamedLink
          name="PreSignupPage"
          params={{ id: author.id.uuid }}
          className={css.primaryButton}
        >
          <FormattedMessage id="SkillSeoPage.viewProfile" />
        </NamedLink>
      )}
    </div>
  );
};

const FaqItem = props => {
  const { question, answer } = props;
  const [expanded, setExpanded] = useState(false);
  const [overflowing, setOverflowing] = useState(false);
  const answerRef = useRef(null);

  useEffect(() => {
    const el = answerRef.current;
    if (el) {
      setOverflowing(el.scrollHeight > el.clientHeight + 1);
    }
  }, []);

  const showToggle = overflowing || expanded;

  return (
    <div className={css.faqItem}>
      <h3 className={css.faqQuestion}>
        <FormattedMessage id={question} />
      </h3>
      <div ref={answerRef} className={expanded ? css.faqAnswerExpanded : css.faqAnswer}>
        <FormattedMessage
          id={answer}
          values={{ lineBreak: <br />, b: chunks => <b>{chunks}</b> }}
        />
      </div>
      {showToggle ? (
        <button type="button" className={css.faqToggle} onClick={() => setExpanded(!expanded)}>
          <FormattedMessage
            id={expanded ? 'SkillSeoPage.faqShowLess' : 'SkillSeoPage.faqShowMore'}
          />
          <IoIosArrowDown className={expanded ? css.faqToggleIconOpen : css.faqToggleIcon} />
        </button>
      ) : null}
    </div>
  );
};

export const SkillSeoPageComponent = props => {
  const {
    content,
    intl,
    scrollingDisabled,
    pageResults,
    reviews,
    offPlatformReviews,
    offPlatformTestimonials,
    skillOptions,
    toolOptions,
    currentUser,
  } = props;

  const schemaTitle = intl.formatMessage({ id: content.schemaTitle });
  const schemaDescription = intl.formatMessage({ id: content.schemaDescription });
  const schemaImage = `${config.canonicalRootURL}${facebookImage}`;

  const freelancers = pageResults.slice(0, MAX_RESULTS);
  const hasResults = freelancers.length >= MIN_RESULTS_TO_SHOW;

  const skillAndToolOptions = skillOptions.concat(toolOptions);
  const translationField = config.locale === 'fi' ? 'fiTranslation' : 'enTranslation';

  const relatedSkillEntries = (content.relatedSkills || [])
    .map(id => skillSeoPageContents.find(entry => entry.id === id))
    .filter(Boolean);
  const relatedSkillKeys = relatedSkillEntries.map(entry => entry.skillKey);

  const handleFaqContactClick = () => {
    if (typeof window === 'object' && window.Intercom) {
      window.Intercom('showSpace', 'home');
    }
  };

  // Experience pill labels for one freelancer: the page's own skill first,
  // then the page's related skills, then the rest.
  const pillLabelsFor = listing => {
    const publicData = listing.attributes.publicData || {};
    const keys = [...(publicData.authorSkillsKeys || []), ...(publicData.authorToolsKeys || [])];
    const orderedKeys = [
      ...keys.filter(k => k === content.skillKey),
      ...relatedSkillKeys.filter(k => keys.includes(k)),
      ...keys.filter(k => k !== content.skillKey && !relatedSkillKeys.includes(k)),
    ];
    return orderedKeys
      .map(key => skillAndToolOptions.find(o => o.key === key)?.[translationField])
      .filter(Boolean);
  };

  const cardDataFor = listing => {
    const authorId = listing.author?.id?.uuid;
    const authorReviews = reviews.filter(
      r => r?.relationships?.subject?.data?.id?.uuid === authorId
    );
    const authorOffPlatformReviews = offPlatformReviews
      .filter(r => r?.freelancerId === authorId)
      .map(r => ({ attributes: { rating: r.rating || r.reviewRating } }));
    const authorTestimonials = offPlatformTestimonials.filter(
      t => t?.freelancerId === authorId && t.state === 'published'
    );
    return {
      reviews: [...authorReviews, ...authorOffPlatformReviews],
      testimonials: authorTestimonials,
    };
  };

  const faqSchema =
    content.faqItems && content.faqItems.length > 0
      ? {
          '@context': 'http://schema.org',
          '@type': 'FAQPage',
          mainEntity: content.faqItems.map(item => ({
            '@type': 'Question',
            name: formatPlainText(intl, item.question),
            acceptedAnswer: {
              '@type': 'Answer',
              text: formatPlainText(intl, item.answer),
            },
          })),
        }
      : null;

  // Mirrors the rendered freelancer cards; null in the fallback state.
  const freelancerItemListSchema = hasResults
    ? buildFreelancerItemListSchema(
        freelancers.map(listing => {
          const author = ensureUser(listing.author);
          const publicData = listing.attributes.publicData || {};
          const variants = author.profileImage?.attributes?.variants;
          return {
            profileUrl: `${config.canonicalRootURL}/u/${author.id.uuid}`,
            displayName: author.attributes.profile?.displayName,
            imageUrl: variants?.['square-small2x']?.url || variants?.['square-small']?.url,
            knowsAbout: pillLabelsFor(listing),
            locationCity: publicData.locationCity,
            locationCountryCode: publicData.locationCountryCode,
          };
        })
      )
    : null;

  const sideCards = (
    <>
      <SatisfactionCard />
      <TestimonialsCard />
      <WhyFreedomlyCard />
    </>
  );

  return (
    <Page
      className={css.root}
      scrollingDisabled={scrollingDisabled}
      contentType="website"
      description={schemaDescription}
      title={schemaTitle}
      facebookImages={[
        { url: `${config.canonicalRootURL}${facebookImage}`, width: 1200, height: 630 },
      ]}
      twitterImages={[
        { url: `${config.canonicalRootURL}${twitterImage}`, width: 600, height: 314 },
      ]}
      schema={[
        {
          '@context': 'http://schema.org',
          '@type': 'WebPage',
          description: schemaDescription,
          name: schemaTitle,
          image: [schemaImage],
        },
        faqSchema,
        freelancerItemListSchema,
      ]}
    >
      <LayoutSingleColumn>
        <LayoutWrapperTopbar>
          <TopbarContainer currentPage={content.name} />
        </LayoutWrapperTopbar>
        <LayoutWrapperMain>
          <div className={css.sections}>
            <section className={css.heroSection}>
              <div className={css.heroContent}>
                <div className={css.heroTextColumn}>
                  <div className={css.heroPill}>
                    <FormattedMessage id={content.heroPill} />
                  </div>
                  <h1 className={css.heroTitle}>
                    <FormattedMessage
                      id={content.heroTitle}
                      values={{
                        lineBreak: <br />,
                        b: chunks => <span className={css.heroTitleHighlight}>{chunks}</span>,
                      }}
                    />
                  </h1>
                  <p className={css.heroSubtitle}>
                    <FormattedMessage id={content.heroSubtitle} />
                  </p>
                  {currentUser ? null : (
                    <NamedLink name="SignupPage" className={css.primaryButton}>
                      <FormattedMessage id="SkillSeoPage.heroCta" />
                    </NamedLink>
                  )}
                </div>
                <div className={css.heroImageColumn}>
                  <div className={css.heroImages}>
                    <img src={heroImage1} alt="" className={css.heroImageSmall} loading="eager" />
                    <img src={heroImage2} alt="" className={css.heroImageLarge} loading="eager" />
                    <img
                      src={heroImage3}
                      alt=""
                      className={css.heroImageSmallRight}
                      loading="eager"
                    />
                  </div>
                  <div className={css.heroNetworkBadge}>
                    <BsFillCheckCircleFill className={css.checkIcon} />
                    <FormattedMessage id="SkillSeoPage.heroNetworkBadge" />
                  </div>
                </div>
              </div>
              <div className={css.heroLogosRow}>
                <div className={css.heroLogosLabel}>
                  <FormattedMessage id="SkillSeoPage.heroLogosLabel" />
                </div>
                <div className={css.heroLogosCarousel}>
                  <div className={css.heroLogosTrack}>
                    {HERO_LOGOS.concat(HERO_LOGOS).map((logo, i) => (
                      <img
                        key={`${logo.id}-${i}`}
                        src={logo.src}
                        alt={`${logo.id} logo`}
                        className={css.heroLogo}
                        loading="lazy"
                      />
                    ))}
                  </div>
                  <div className={css.heroLogosFadeLeft} />
                  <div className={css.heroLogosFadeRight} />
                </div>
              </div>
            </section>

            {hasResults ? (
              <section className={css.resultsSection}>
                <div className={css.resultsSidebar}>{sideCards}</div>
                <div className={css.resultsColumn}>
                  {freelancers.map(listing => {
                    const cardData = cardDataFor(listing);
                    return (
                      <FreelancerCard
                        key={listing.id.uuid}
                        listing={listing}
                        reviews={cardData.reviews}
                        testimonials={cardData.testimonials}
                        pillLabels={pillLabelsFor(listing)}
                        isAuthenticated={!!currentUser}
                      />
                    );
                  })}
                  <div className={css.findMoreBlock}>
                    <h3 className={css.findMoreTitle}>
                      <FormattedMessage id={content.findMoreFreelancersText} />
                    </h3>
                    {currentUser ? (
                      <NamedLink name="NewProjectListingPage" className={css.primaryButton}>
                        <FormattedMessage id="SkillSeoPage.findMoreCtaAuthenticated" />
                      </NamedLink>
                    ) : (
                      <NamedLink name="SignupPage" className={css.primaryButton}>
                        <FormattedMessage id="SkillSeoPage.findMoreCta" />
                      </NamedLink>
                    )}
                  </div>
                </div>
              </section>
            ) : (
              <section className={css.noResultsSection}>{sideCards}</section>
            )}

            <section className={css.howItWorksSection}>
              <h2 className={css.howItWorksTitle}>
                <FormattedMessage id="SkillSeoPage.howItWorksTitle" />
              </h2>
              <div className={css.howItWorksSteps}>
                {HOW_IT_WORKS_STEPS.map(step => (
                  <div key={step.titleId} className={css.stepCard}>
                    <div className={css.stepCardContent}>
                      <h3 className={css.stepCardTitle}>
                        <FormattedMessage id={step.titleId} />
                      </h3>
                      <p className={css.stepCardText}>
                        <FormattedMessage id={step.textId} />
                      </p>
                    </div>
                    <div className={css.stepCardCheckRow}>
                      <BsFillCheckCircleFill className={css.stepCheckIcon} />
                      <FormattedMessage id={step.checkId} />
                    </div>
                  </div>
                ))}
              </div>
              <NamedLink
                name={currentUser ? 'BrowseFreelancersPage' : 'SignupPage'}
                className={css.primaryButton}
              >
                <FormattedMessage id="SkillSeoPage.howItWorksCta" />
              </NamedLink>
            </section>

            {relatedSkillEntries.length > 0 ? (
              <section className={css.relatedSkillsSection}>
                <h2 className={css.relatedSkillsTitle}>
                  <FormattedMessage id="SkillSeoPage.relatedSkillsTitle" />
                </h2>
                <div className={css.relatedSkillsPills}>
                  {relatedSkillEntries.map(entry => (
                    <NamedLink key={entry.id} name={entry.name} className={css.relatedSkillPill}>
                      <FormattedMessage id={entry.heroPill} />
                    </NamedLink>
                  ))}
                </div>
              </section>
            ) : null}

            <section className={css.faqSection}>
              <div className={css.faqIntroColumn}>
                <div className={css.faqLabel}>
                  <FormattedMessage id="SkillSeoPage.faqLabel" />
                </div>
                <h2 className={css.faqTitle}>
                  <FormattedMessage id={content.faqTitle} />
                </h2>
                <p className={css.faqHelpText}>
                  <FormattedMessage id="SkillSeoPage.faqHelpText" />
                </p>
                <button type="button" className={css.primaryButton} onClick={handleFaqContactClick}>
                  <FormattedMessage id="SkillSeoPage.faqContactButton" />
                </button>
              </div>
              <div className={css.faqItems}>
                {content.faqItems.map(item => (
                  <FaqItem key={item.question} question={item.question} answer={item.answer} />
                ))}
              </div>
            </section>
          </div>
          <JoinPreFooter showContactButton />
        </LayoutWrapperMain>
        <LayoutWrapperFooter>
          <Footer />
        </LayoutWrapperFooter>
      </LayoutSingleColumn>
    </Page>
  );
};

SkillSeoPageComponent.defaultProps = {
  pageResults: [],
  reviews: [],
  offPlatformReviews: [],
  offPlatformTestimonials: [],
  skillOptions: [],
  toolOptions: [],
};

SkillSeoPageComponent.propTypes = {
  content: object.isRequired,
  scrollingDisabled: bool.isRequired,
  pageResults: arrayOf(object),
  reviews: arrayOf(object),
  offPlatformReviews: arrayOf(object),
  offPlatformTestimonials: arrayOf(object),

  // from withRouter
  history: object.isRequired,
  location: object.isRequired,

  // from injectIntl
  intl: intlShape.isRequired,
};

const mapStateToProps = state => {
  const {
    currentPageResultIds,
    queryInProgress,
    queryFreelancersError,
    reviews,
    offPlatformReviews,
    offPlatformTestimonials,
  } = state.SkillSeoPage;

  const { skillOptions, toolOptions } = state.SkillsAndTools;

  return {
    scrollingDisabled: isScrollingDisabled(state),
    currentUser: state.user.currentUser,
    pageResults: getListingsById(state, currentPageResultIds),
    queryInProgress,
    queryFreelancersError,
    reviews,
    offPlatformReviews,
    offPlatformTestimonials,
    skillOptions,
    toolOptions,
  };
};

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const SkillSeoPage = compose(
  withRouter,
  connect(mapStateToProps),
  injectIntl
)(SkillSeoPageComponent);

SkillSeoPage.loadData = (params, search, content) => loadData(content);

export default SkillSeoPage;
